import { useEffect, useState } from "react";
import { themeVar as colors } from "../design-tokens";
import { normalizeStatus } from "./constants";
import { cumulativeDaysOnMarketLabel, daysOnMarketLabel } from "./daysOnMarket";
import type { CandidateComp } from "./types";

/**
 * What the upload contained that is NOT a comparable sale.
 *
 * THE CASE THIS EXISTS FOR. An agent sends one MLS export with six properties on it — three closed,
 * three still for sale — plus the subject's original listing from years ago for its property
 * details. Before this, all seven landed in one comp table. The three still-listed homes were
 * either silently dropped as "missing sale price" or, worse, had their ASKING price priced as a
 * closed sale. The subject's own old listing scored as a near-perfect match to itself, ranked first,
 * and arrived pre-checked.
 *
 * The backend now separates all three groups. This renders that separation so the agent can SEE it:
 * six rows in, three priced, three shown as competition, one set aside as their own history — with a
 * reason on every one. A number that quietly changes is the thing that loses trust; a number that
 * changes with a stated reason is the product working.
 *
 * SELECTABLE SINCE 2026-08-04 — and the distinction matters. The original rule here was "no
 * checkbox anywhere, because there is no honest way for a human to promote an asking price into a
 * set of closed sales." That rule is untouched and is enforced somewhere far stronger than a UI:
 * `engine.amb.select_best` filters to closed sales, `screen()` drops competing rows with a stated
 * reason, and the server re-checks every submitted active against the engine rather than trusting
 * the client. NOTHING here can move a number.
 *
 * What these checkboxes decide is a DIFFERENT question the agent was never asked: which of their
 * competition appears in the report. An export with nine active listings printed all nine, and an
 * agent presenting to a seller wants the three their listing will actually launch against — not a
 * page of everything the MLS returned. The best-fit rows arrive ticked (the server has already
 * ranked them), and the agent adds or removes any of them.
 *
 * The copy on screen has to keep those two facts apart, because they are easy to blur: ticking a
 * row puts it on the page, and it still has no effect whatever on the value.
 */

function money(v: number | null | undefined): string {
  return typeof v === "number" ? `$${v.toLocaleString()}` : "—";
}

/**
 * THE COMPETITION IS DESCRIBED, NOT JUST LISTED (2026-08-04).
 *
 * This table showed address, status, asking price, GLA, year and days on market — and stopped.
 * The comparable grid directly above it shows lot size, bed and bath counts and garage bays on
 * every row, so an agent reading down the page got a full description of each SOLD home and a
 * near-anonymous line for each home they are about to compete with. That is backwards for the
 * conversation this page exists to support: the seller's question is "how does my house compare
 * to the ones a buyer will see this weekend", and it cannot be answered from a price and a size.
 *
 * The data was already there. `CandidateComp` carries `lot_sf`, `beds`, `full_baths`,
 * `half_baths` and `garage` on every row the extractor produces, competing or closed, and this
 * component simply did not render them. Nothing new is fetched and no value is computed.
 *
 * A BLANK STAYS BLANK. Every cell falls back to an em dash rather than a zero, for the same
 * reason the comp grid does: an unstated field is unknown, not none, and an active listing is
 * NEVER priced — so an unread field here can never reach a valuation. That is exactly why these
 * cells are read-only display while the comp grid's are inputs. Adding an editable box here
 * would imply the number matters to the estimate; it does not, and never can.
 */
function num(v: unknown): string {
  return typeof v === "number" && Number.isFinite(v) ? v.toLocaleString() : "—";
}

function baths(c: CandidateComp): string {
  const f = typeof c.full_baths === "number" ? c.full_baths : null;
  const h = typeof c.half_baths === "number" ? c.half_baths : null;
  if (f === null && h === null) return "—";
  return `${f ?? "—"} / ${h ?? 0}`;
}

/**
 * One entry per member of COMPETING_STATUSES, with the SAME wording as the server's
 * `amb_build._COMPETING_STATUS_LABEL`. This map fell behind once: the server was widened to six
 * competing statuses and this was left at three, so a contingent / kick-out / coming-soon listing
 * read "On the market" on the review screen while the PDF the agent downloaded said "Active
 * contingent" — the two halves of the product describing the same row differently.
 * `CompetingListings.test.tsx` now pins every competing status to a label.
 *
 * Off-market statuses are deliberately absent: a withdrawn or expired listing is not competition
 * and never reaches this table.
 */
export const STATUS_LABEL: Record<string, string> = {
  active: "Active",
  pending: "Pending",
  active_option_contract: "Active option contract",
  active_contingent: "Active contingent",
  active_kick_out: "Active kick out",
  coming_soon: "Coming soon",
};

/**
 * Phone width, as a RENDER decision rather than a paint one, so every checkbox and address is in
 * the document exactly once (a hidden duplicate would double every accessible name). `matchMedia`
 * absent (jsdom without the stub, any non-browser render) answers WIDE: the full table.
 */
function useNarrow(px = 600): boolean {
  const query = `(max-width: ${px}px)`;
  const [narrow, setNarrow] = useState(() =>
    typeof window !== "undefined" && window.matchMedia ? window.matchMedia(query).matches : false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia(query);
    const on = (e: MediaQueryListEvent) => setNarrow(e.matches);
    setNarrow(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, [query]);
  return narrow;
}

export function CompetingListings({
  listings,
  onListingsChange,
  contextOnly = false,
  hidden,
  order,
  reasons,
}: {
  /** The competing-homes search (2026-11-03): listing indices its filters hide, the order to paint
   * (indices into `listings`) and the ranking reason per index. The array and every index are
   * untouched; only the rows painted, and their order, change. Omit all three: exactly as before. */
  hidden?: Set<number>;
  order?: number[];
  reasons?: Record<number, string>;
  listings: CandidateComp[];
  /** R7 acreage mode: label the section "context only". Off (the default) renders exactly as before. */
  contextOnly?: boolean;
  /** Omit to render exactly as before, read-only — the review/summary screens want that. */
  onListingsChange?: (next: CandidateComp[]) => void;
}) {
  const narrow = useNarrow();
  if (!listings.length) return null;
  const selectable = typeof onListingsChange === "function";
  const shown = listings.filter((c) => c._selected !== false).length;
  /* THE NEIGHBOURHOOD, ON THE COMPETITION TOO (2026-08-10, owner: "on the active properties we
     need to show the neighborhood").

     The comp grid has had this column since 2026-08-04 for the reason that applies at least as
     strongly here: whether a home is in the same pocket of the market is most of what decides
     whether it is really competing with the subject. Two actives at the same asking price are not
     the same competition when one is across a boundary a buyer prices differently.

     THE COLUMN APPEARS ONLY WHEN SOMETHING IS IN IT, and that condition is not defensive
     housekeeping — it is the honest handling of a field that genuinely is not always there. The
     extractor asks for `subdivision` on every record and the AI read fills it in (on the owner's
     Springtree file all five competing listings carry one), but the DETERMINISTIC fallback parser
     — the path taken whenever the AI step is skipped or unavailable — does not populate it at all,
     so on those files every value is null. A column of nothing but em dashes claims we looked for
     something and each home lacked it; showing no column at all is the truthful version of "this
     read did not carry neighbourhoods". Individual blanks within a populated column still render
     as a dash, the same as every other cell here. */
  const anyNeighborhood = listings.some((c) => (c.subdivision ?? "").trim() !== "");
  return (
    <section
      aria-labelledby="vb-competing-heading"
      style={{ marginTop: 24, border: `1px solid ${colors.border}`, borderRadius: 10, padding: 18, background: colors.bgSurface }}
    >
      <h2 id="vb-competing-heading" style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>
        Currently on the market — {listings.length} {listings.length === 1 ? "home" : "homes"}
        {selectable && listings.length !== shown ? ` (${shown} in your report)` : ""}
      </h2>
      {contextOnly && (
        <p data-testid="listings-context-only" style={{ marginTop: 6, marginBottom: 0, fontSize: 13, fontWeight: 600 }}>
          Context only: active and pending listings, and land listings, are never priced in an acreage search.
        </p>
      )}
      {narrow ? (
        <details className="vb-cl-howto" style={{ marginTop: 6 }}>
          <summary style={{ fontSize: 13, color: colors.textSecondary, cursor: "pointer" }}>How these are used</summary>
      <p style={{ marginTop: 6, marginBottom: 0, fontSize: 13, color: colors.textSecondary, lineHeight: 1.5 }}>
        These came from your file and are your subject&apos;s competition. An asking price is what a
        seller hopes for, so none of them is used to calculate the value — that comes from closed
        sales only.{" "}
        {selectable
          ? "Tick the ones you want shown in the report as the competition your listing will launch against. The closest matches are ticked already; ticking or unticking changes what appears on the page and nothing about the value."
          : "They appear in the report as the competition your listing will launch against."}
      </p>
        </details>
      ) : (
      <p style={{ marginTop: 6, marginBottom: 0, fontSize: 13, color: colors.textSecondary, lineHeight: 1.5 }}>
        These came from your file and are your subject&apos;s competition. An asking price is what a
        seller hopes for, so none of them is used to calculate the value — that comes from closed
        sales only.{" "}
        {selectable
          ? "Tick the ones you want shown in the report as the competition your listing will launch against. The closest matches are ticked already; ticking or unticking changes what appears on the page and nothing about the value."
          : "They appear in the report as the competition your listing will launch against."}
      </p>
      )}

      {narrow ? (
        <>
          <ul className="vb-cl-cards" style={{ listStyle: "none", margin: "12px 0 0", padding: 0, display: "grid", gap: 10 }}>
            {listings.map((c, i) => (
              <li key={`${c.name ?? "listing"}-${i}`} data-listing-card
                  style={{ border: `1px solid ${colors.border}`, borderRadius: 8, padding: 12, display: "grid", gap: 6, minWidth: 0 }}>
                <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                  {selectable && (
                    <input
                      type="checkbox"
                      checked={c._selected !== false}
                      aria-label={`Show ${c.name ?? "this listing"} in the report`}
                      onChange={(e) => {
                        const next = listings.slice();
                        next[i] = { ...c, _selected: e.target.checked };
                        onListingsChange!(next);
                      }}
                    />
                  )}
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <p style={{ margin: 0, fontWeight: 600, fontSize: 14, overflowWrap: "anywhere" }}>{c.name ?? "—"}</p>
                    {anyNeighborhood && (
                      <p style={{ margin: "2px 0 0", fontSize: 12, color: colors.textSecondary }}>{(c.subdivision ?? "").trim() || "—"}</p>
                    )}
                  </div>
                  <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 999, fontSize: 12, background: colors.amberBg, color: colors.amber, whiteSpace: "nowrap" }}>
                    {STATUS_LABEL[normalizeStatus(c.status)] ?? "On the market"}
                  </span>
                </div>
                <p style={{ margin: 0, fontSize: 12, color: colors.textSecondary }}>Asking</p>
                <p style={{ margin: "-6px 0 0", fontFamily: "monospace", fontSize: 18, fontWeight: 600 }}>{money(c.list_price)}</p>
                <p style={{ margin: 0, fontSize: 13, color: colors.textSecondary, lineHeight: 1.5 }}>
                  {num(c.gla)} SF · {c.beds ?? "—"} bd · {baths(c)} ba · {num(c.lot_sf)} SF lot · Built {c.year ?? "—"} · {c.garage ?? "—"} garage
                </p>
                <p style={{ margin: 0, fontSize: 13 }}>
                  <span style={{ color: colors.textSecondary }}>Days on market </span>
                  <span style={{ fontFamily: "monospace" }}>{daysOnMarketLabel(c, c.dom_as_of)}</span>
                  {cumulativeDaysOnMarketLabel(c) && (
                    <span style={{ fontSize: 11, color: colors.textSecondary }}> ({cumulativeDaysOnMarketLabel(c)})</span>
                  )}
                </p>
              </li>
            ))}
          </ul>
          <p style={{ margin: "8px 0 0", fontSize: 12, color: colors.textSecondary }}>
            Homes on the market now. Not part of the valuation, whether ticked or not.
          </p>
        </>
      ) : (
      <div style={{ overflowX: "auto", overscrollBehaviorX: "contain", marginTop: 12 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 860 }}>
          <caption style={{ captionSide: "bottom", textAlign: "left", fontSize: 12, color: colors.textSecondary, paddingTop: 8 }}>
            Homes on the market now. Not part of the valuation, whether ticked or not.
          </caption>
          <thead>
            <tr style={{ textAlign: "left", color: colors.textSecondary, borderBottom: `1px solid ${colors.border}` }}>
              {selectable && <th scope="col" style={{ padding: "6px 8px" }}><span style={{ position: "absolute", left: -9999 }}>Show in report</span></th>}
              <th scope="col" style={{ padding: "6px 8px" }}>Address</th>
              {anyNeighborhood && <th scope="col" style={{ padding: "6px 8px" }}>Neighborhood</th>}
              <th scope="col" style={{ padding: "6px 8px" }}>Status</th>
              <th scope="col" style={{ padding: "6px 8px" }}>Asking</th>
              <th scope="col" style={{ padding: "6px 8px" }}>GLA</th>
              <th scope="col" style={{ padding: "6px 8px" }}>Lot (SF)</th>
              <th scope="col" style={{ padding: "6px 8px" }}>Year</th>
              <th scope="col" style={{ padding: "6px 8px" }}>Beds</th>
              <th scope="col" style={{ padding: "6px 8px" }}>Baths (F/H)</th>
              <th scope="col" style={{ padding: "6px 8px" }}>Garage</th>
              <th scope="col" style={{ padding: "6px 8px" }}>Days on market</th>
            </tr>
          </thead>
          <tbody>
            {(order && order.length === listings.length ? order : listings.map((_, i) => i)).map((i) => {
              const c = listings[i];
              return hidden?.has(i) ? null : (
              <tr key={`${c.name ?? "listing"}-${i}`} style={{ borderTop: `1px solid ${colors.border}` }}>
                {selectable && (
                  <td style={{ padding: 8 }}>
                    <input
                      type="checkbox"
                      checked={c._selected !== false}
                      aria-label={`Show ${c.name ?? "this listing"} in the report`}
                      onChange={(e) => {
                        const next = listings.slice();
                        next[i] = { ...c, _selected: e.target.checked };
                        onListingsChange!(next);
                      }}
                    />
                  </td>
                )}
                <th scope="row" style={{ padding: 8, fontWeight: 500, textAlign: "left" }}>
                  {c.name ?? "—"}
                  {reasons?.[i] && (
                    <div data-testid="listing-reason" style={{ fontSize: 11, fontWeight: 400, color: colors.textSecondary }}>{reasons[i]}</div>
                  )}
                </th>
                {/* Read-only, like every cell in this table and like the same column in the comp
                    grid: a typed neighbourhood would be a claim about where a house is. */}
                {anyNeighborhood && <td style={{ padding: 8 }}>{(c.subdivision ?? "").trim() || "—"}</td>}
                <td style={{ padding: 8 }}>
                  <span
                    style={{
                      display: "inline-block", padding: "2px 8px", borderRadius: 999, fontSize: 12,
                      background: colors.amberBg, color: colors.amber,
                    }}
                  >
                    {STATUS_LABEL[normalizeStatus(c.status)] ?? "On the market"}
                  </span>
                </td>
                <td style={{ padding: 8, fontFamily: "monospace" }}>{money(c.list_price)}</td>
                <td style={{ padding: 8, fontFamily: "monospace" }}>{num(c.gla)}</td>
                <td style={{ padding: 8, fontFamily: "monospace" }}>{num(c.lot_sf)}</td>
                <td style={{ padding: 8, fontFamily: "monospace" }}>{c.year ?? "—"}</td>
                <td style={{ padding: 8, fontFamily: "monospace" }}>{c.beds ?? "—"}</td>
                <td style={{ padding: 8, fontFamily: "monospace" }}>{baths(c)}</td>
                <td style={{ padding: 8, fontFamily: "monospace" }}>{c.garage ?? "—"}</td>
                {/* Same rule as the comp grid: DOM when printed, CDOM when only that is, and an
                    ACTIVE listing's figure is brought forward to today — this table is the seller's
                    competition, and a stale exposure figure misstates it by the age of the file. */}
                <td style={{ padding: 8, fontFamily: "monospace", whiteSpace: "nowrap" }}>
                  {daysOnMarketLabel(c, c.dom_as_of)}
                  {cumulativeDaysOnMarketLabel(c) && (
                    <div style={{ fontSize: 11, color: colors.textSecondary, fontFamily: "system-ui, sans-serif" }}>
                      {cumulativeDaysOnMarketLabel(c)}
                    </div>
                  )}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      )}
    </section>
  );
}

/**
 * The subject's own prior sale, when the upload contained one.
 *
 * A property cannot be its own comparable, so it is out of the grid. It is not discarded, though:
 * it is real, sourced history for this home, and the report may state it as context. Showing it here
 * is also the fastest way for the agent to confirm the app identified their subject correctly.
 */
export function PriorSaleNotice({ priorSales }: { priorSales: CandidateComp[] }) {
  if (!priorSales.length) return null;
  return (
    <section
      style={{ marginTop: 16, border: `1px solid ${colors.border}`, borderLeft: `3px solid ${colors.accent}`, borderRadius: 8, padding: "12px 14px", background: colors.accentSoft }}
    >
      <p style={{ margin: 0, fontSize: 13, fontWeight: 600 }}>
        Your subject&apos;s own earlier listing was in the upload
      </p>
      <ul style={{ margin: "6px 0 0", paddingLeft: 18, fontSize: 13, color: colors.textSecondary, lineHeight: 1.6 }}>
        {priorSales.map((c, i) => (
          <li key={`${c.name ?? "prior"}-${i}`}>
            {c.name ?? "This property"} — sold {money(c.price)}
            {c.sale_date ? ` (${c.sale_date})` : ""}
          </li>
        ))}
      </ul>
      <p style={{ margin: "8px 0 0", fontSize: 12, color: colors.textSecondary, lineHeight: 1.5 }}>
        A home is never its own comparable, so this is not in the grid and does not affect the
        estimate. Its property details still help fill gaps, and the past sale can appear in the
        report as history.
      </p>
    </section>
  );
}

/**
 * What the reader was told while the files were read — the `gaps` the server has always assembled
 * and no client has ever rendered. A field it could not find, a comp it set aside and why, how many
 * uploaded homes are still listed. Shown calmly: a disclosed gap is the honest result, and hiding it
 * is what turns a gap into a surprise at the worst moment.
 */
export function ReadingNotes({ gaps }: { gaps: string[] }) {
  if (!gaps.length) return null;
  return (
    <section
      aria-labelledby="vb-reading-notes"
      style={{ marginTop: 16, border: `1px solid ${colors.border}`, borderRadius: 8, padding: "12px 14px", background: colors.bgApp }}
    >
      <h3 id="vb-reading-notes" style={{ margin: 0, fontSize: 13, fontWeight: 600 }}>
        What we noticed while reading your files
      </h3>
      <ul style={{ margin: "6px 0 0", paddingLeft: 18, fontSize: 13, color: colors.textSecondary, lineHeight: 1.6 }}>
        {gaps.map((g, i) => (
          <li key={i}>{g}</li>
        ))}
      </ul>
    </section>
  );
}
